import React from "react";
import { Link } from "react-router-dom";
import "./legal.css";
import { ReactComponent as MainLogo } from "../../assets/signin/Logo.svg";

// Public, no sign-in: Google Play links to this page from the store listing.
// Keep it in step with what the server actually collects (models/Member.js)
// and who it shares it with -- if a new service starts receiving member data,
// it belongs in the "Who else handles your data" table.
const CONTACT_EMAIL = "youthalliancefund@gmail.com";
const LAST_UPDATED = "28 October 2026";

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <div className="legal-column">
        <MainLogo className="legal-logo" />
        <h1>Privacy policy</h1>
        <p className="legal-updated">Last updated {LAST_UPDATED}</p>

        <p>
          SwitchFund is an app for running a members' savings fund: recording
          what each member saves, the loans the fund makes to its members and
          their repayments, and the fund's accounts. It is operated by Youth
          Alliance Fund, Sirsi, Karnataka, India ("we"). This policy covers the
          SwitchFund Android app, the iOS app and the web console at
          app.switchfund.in.
        </p>
        <p>
          SwitchFund is a record-keeping tool for a fund and its members. It
          does not lend money itself, and it does not move money: payments are
          made between members and their fund outside the app and recorded in
          it.
        </p>

        <h2>What we collect</h2>
        <table className="legal-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Name, email address, password</td>
              <td>
                Your account, and signing in. The password is stored only as a
                one-way hash; nobody, including us, can read it.
              </td>
            </tr>
            <tr>
              <td>Phone number, address, city, state, PIN code, nationality, age, gender, profile photo</td>
              <td>
                Your member profile, which the officers of your fund use to
                identify and contact members.
              </td>
            </tr>
            <tr>
              <td>Bank name, account holder name, account number, IFSC code, UPI ID</td>
              <td>
                So your fund can pay you (a sanctioned loan, a withdrawal).
                Stored encrypted.
              </td>
            </tr>
            <tr>
              <td>Savings, loans, loan applications, repayments, interest, payments, vouchers and invoices</td>
              <td>
                The fund's accounts — the purpose of the app. Entered by you or
                your fund's officers.
              </td>
            </tr>
            <tr>
              <td>Documents your fund uploads</td>
              <td>Shared with the members of that fund.</td>
            </tr>
            <tr>
              <td>A push notification token for your device</td>
              <td>
                To send you your fund's announcements and updates on your loan
                applications. Removed when you sign out.
              </td>
            </tr>
            <tr>
              <td>Server logs: the requests made to our server, their time and IP address</td>
              <td>Keeping the service secure and finding faults.</td>
            </tr>
          </tbody>
        </table>
        <p>
          We do not use advertising, we do not track you across other apps or
          websites, and we do not sell or rent your data to anybody.
        </p>

        <h2>Who can see your data</h2>
        <ul>
          <li>
            <strong>Your fund.</strong> Members and officers of a fund you
            belong to can see your name and your records in that fund, as far
            as the roles your fund has set up allow. Officers whose role permits
            it can see your contact and bank details. Other funds cannot see
            any of it.
          </li>
          <li>
            <strong>Service providers</strong> that run parts of SwitchFund for
            us, only for that purpose:
          </li>
        </ul>
        <table className="legal-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>What for</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Amazon Web Services</td>
              <td>Runs the SwitchFund server.</td>
            </tr>
            <tr>
              <td>MongoDB Atlas</td>
              <td>The database holding accounts and fund records.</td>
            </tr>
            <tr>
              <td>Google Firebase</td>
              <td>Push notifications, and storage of photos and documents.</td>
            </tr>
            <tr>
              <td>Google (Gmail)</td>
              <td>Sending emails: invitations, password reset codes, statements.</td>
            </tr>
            <tr>
              <td>Carbone</td>
              <td>Producing invoice and receipt PDFs.</td>
            </tr>
            <tr>
              <td>Better Stack (Logtail)</td>
              <td>Server logs. Passwords and bank details are never logged.</td>
            </tr>
          </tbody>
        </table>
        <p>
          We may also disclose data where the law requires it, for example to
          comply with a valid order of a court or government authority.
        </p>

        <h2>How we protect it</h2>
        <ul>
          <li>Everything sent between the app and our server is encrypted (HTTPS).</li>
          <li>Passwords are hashed; bank details are encrypted in the database.</li>
          <li>
            Access inside a fund is controlled by roles and permissions, and
            changing your password signs out every other device.
          </li>
        </ul>

        <h2>How long we keep it</h2>
        <p>
          Your account is kept for as long as you have it. When you delete it,
          your email, password, phone number, address, age, gender, photo, bank
          details and UPI ID are deleted straight away, and you are removed
          from every fund.
        </p>
        <p>
          Your name, your member ID and the fund's records of your
          transactions are kept, because they are part of the fund's accounts:
          every other member's balances depend on them, and a fund has to keep
          its accounts. They are kept for as long as the fund keeps its
          records.
        </p>

        <h2>Your rights</h2>
        <ul>
          <li>See and correct your details at any time from your profile in the app.</li>
          <li>
            Delete your account in the app (Profile → Delete account) or on
            the web at{" "}
            <Link to="/delete-account">app.switchfund.in/delete-account</Link>.
          </li>
          <li>
            Ask us what data we hold about you, or for anything else about
            this policy, by writing to{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We reply
            within 30 days.
          </li>
        </ul>

        <h2>Children</h2>
        <p>SwitchFund is for adults. It is not meant for anybody under 18.</p>

        <h2>Changes</h2>
        <p>
          If we change this policy we will update the date at the top, and
          tell members in the app when the change is significant.
        </p>

        <h2>Contact</h2>
        <p>
          Youth Alliance Fund, Sirsi, Karnataka, India
          <br />
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </p>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
